<script setup>
import { ref, watch, computed, onUnmounted, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import RecursiveMenuItem from './RecursiveMenuItem.vue';
import CollapsedSubmenuPanel from './CollapsedSubmenuPanel.vue';
import { ToolOutlined } from '@ant-design/icons-vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    filterRoutesByPermissions,
    checkRoutePermission
} from '@/router/permissions';

const props = defineProps({
    collapsed: {
        type: Boolean,
        required: true
    },
    isMobile: {
        type: Boolean,
        required: true
    }
});

const router = useRouter();
const route = useRoute();
const adminStore = useAdminInfo();
const openKeys = ref([]);
const selectedKeys = ref([]);

// 開發者模式相關
const developerMode = ref(false);
const clickCount = ref(0);
const lastClickTime = ref(0);

// 使用計算屬性來獲取選單項目，當權限變化時會自動更新
const menuItems = computed(() => {
    // 如果權限還沒載入，返回空陣列（避免顯示錯誤的選單）
    if (!adminStore.isLoaded) {
        return [];
    }

    const allRoutes = router.getRoutes();
    const rootRoute = allRoutes.find((r) => r.path === '/' && r.children);

    // 先過濾出有標題的路由
    const filteredRoutes =
        rootRoute?.children?.filter(
            (route) => route.name && route.meta?.title && !route.meta?.hidden
        ) || [];

    // 直接使用反應式的權限清單，確保權限變化時會觸發重新計算
    const permissions = adminStore.permissions || [];

    // Dashboard 路由也需要檢查權限
    const dashboardRoute = filteredRoutes.find((r) => r.name === 'Dashboard');
    const otherRoutes = filteredRoutes.filter((r) => r.name !== 'Dashboard');

    // 根據權限過濾路由（包括 Dashboard）；admin 角色全開
    const menuOptions = {
        forMenu: true,
        roles: adminStore.roles || []
    };
    const permittedRoutes = filterRoutesByPermissions(
        otherRoutes,
        permissions,
        menuOptions
    );

    // 檢查 Dashboard 權限
    const hasDashboardPermission = dashboardRoute
        ? checkRoutePermission('Dashboard', permissions, menuOptions)
        : false;

    // 將 Dashboard 放在最前面（如果有權限）
    const dashboardRoutes =
        hasDashboardPermission && dashboardRoute ? [dashboardRoute] : [];
    return [...dashboardRoutes, ...permittedRoutes];
});

// 收合側欄：單一浮動子選單（避免每個 icon 各掛 a-popover 造成重複彈層）
const activePopoverKey = ref(null);
const collapsedFloatingStyle = ref({ top: '0px', left: '0px' });
let outsideClickHandler = null;

const activePopoverRoute = computed(
    () =>
        menuItems.value.find((item) => item.name === activePopoverKey.value) ||
        null
);

const updateCollapsedFloatingPosition = (triggerEl) => {
    if (!(triggerEl instanceof HTMLElement)) {
        return;
    }
    const rect = triggerEl.getBoundingClientRect();
    const panelWidth = 220;
    const viewportPadding = 8;
    let left = rect.right + 8;
    if (left + panelWidth > window.innerWidth - viewportPadding) {
        left = Math.max(viewportPadding, rect.left - panelWidth - 8);
    }
    const maxTop = Math.max(viewportPadding, window.innerHeight - 320);
    const top = Math.min(Math.max(viewportPadding, rect.top), maxTop);

    collapsedFloatingStyle.value = {
        top: `${top}px`,
        left: `${left}px`
    };
};

const removeOutsideClick = () => {
    if (outsideClickHandler) {
        document.removeEventListener('click', outsideClickHandler, true);
        outsideClickHandler = null;
    }
};

const closeCollapsedPopover = () => {
    activePopoverKey.value = null;
    removeOutsideClick();
};

const registerOutsideClick = () => {
    removeOutsideClick();
    outsideClickHandler = (event) => {
        const target = event.target;
        if (!(target instanceof Node)) {
            closeCollapsedPopover();
            return;
        }
        const inTrigger = target.closest?.('.collapsed-popover-trigger');
        const inPopover = target.closest?.('.collapsed-submenu-floating');
        if (!inTrigger && !inPopover) {
            closeCollapsedPopover();
        }
    };
    document.addEventListener('click', outsideClickHandler, true);
};

const toggleCollapsedPopover = (key, event) => {
    event?.stopPropagation?.();
    if (activePopoverKey.value === key) {
        closeCollapsedPopover();
        return;
    }
    updateCollapsedFloatingPosition(event?.currentTarget);
    activePopoverKey.value = key;
    nextTick(() => registerOutsideClick());
};

const onCollapsedPopoverKeydown = (event) => {
    if (event.key === 'Escape') {
        closeCollapsedPopover();
    }
};

const onCollapsedViewportChange = () => {
    closeCollapsedPopover();
};

watch(activePopoverKey, (key) => {
    if (key) {
        window.addEventListener('scroll', onCollapsedViewportChange, true);
        window.addEventListener('resize', onCollapsedViewportChange);
        return;
    }
    window.removeEventListener('scroll', onCollapsedViewportChange, true);
    window.removeEventListener('resize', onCollapsedViewportChange);
});

// 收合側欄彈出選單：保留與展開狀態相同的父子層級
const getCollapsedSubmenuTree = (routeName) => {
    const allRoutes = router.getRoutes();
    const targetRoute = allRoutes.find((route) => route.name === routeName);

    if (targetRoute?.children?.length) {
        const permissions = adminStore.permissions || [];
        return filterRoutesByPermissions(targetRoute.children, permissions, {
            forMenu: true,
            roles: adminStore.roles || []
        });
    }

    return [];
};

// 根據當前路徑設置選中的選單項
const updateSelectedKeys = (path) => {
    const currentRoute = router.resolve(path);
    const matched = currentRoute.matched;

    // 更新 selectedKeys
    selectedKeys.value = [`item-${matched[matched.length - 1].name}`];

    // 更新 openKeys，包含所有父層級
    openKeys.value = matched
        .slice(1, -1) // 排除根路由和當前路由
        .map((route) => `sub-${route.name}`);
};

// 監聽路由變化
watch(
    () => route.path,
    (newPath) => {
        updateSelectedKeys(newPath);
        closeCollapsedPopover();
    },
    { immediate: true }
);

// 處理選單展開收合
const handleOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => !openKeys.value.includes(key));

    if (latestOpenKey) {
        // 找到當前路由的所有父層級選單
        const currentRoute = router.resolve(route.path);
        const parentKeys = currentRoute.matched
            .slice(1, -1)
            .map((route) => `sub-${route.name}`);

        // 確保父層級選單保持展開
        openKeys.value = [...new Set([...parentKeys, latestOpenKey])];
    } else {
        openKeys.value = keys;
    }
};

// 開發者模式觸發
const handleDeveloperModeClick = () => {
    const now = Date.now();

    // 如果距離上次點擊超過 2 秒，重置計數
    if (now - lastClickTime.value > 2000) {
        clickCount.value = 1;
    } else {
        clickCount.value++;
    }

    lastClickTime.value = now;

    // 連續點擊三次觸發開發者模式
    if (clickCount.value >= 3) {
        developerMode.value = !developerMode.value;
        clickCount.value = 0;

        if (developerMode.value) {
            console.log('🔧 開發者模式已啟用');

            // // 動態添加 Example 路由到主路由
            // const exampleRoute = {
            //     path: '/example',
            //     name: 'Example',
            //     component: () => import('@/components/vaporwave/Example.vue'),
            //     meta: {
            //         title: '元件範例',
            //         icon: 'ExperimentOutlined'
            //     }
            // };

            // 檢查路由是否已存在
            const existingRoute = router
                .getRoutes()
                .find((r) => r.name === 'Example');
            if (!existingRoute) {
                // 找到主路由（Layout）並添加子路由
                const mainRoute = router
                    .getRoutes()
                    .find((r) => r.path === '/');
                if (mainRoute) {
                    router.addRoute('/', exampleRoute);
                } else {
                    // 如果找不到主路由，直接添加到根級別
                    router.addRoute(exampleRoute);
                }
            }

            // 導航到 Example 頁面
            router.push('/example');
        } else {
            console.log('🔧 開發者模式已關閉');
            // 導航到控制台
            router.push('/dashboard');
        }
    }
};

// 獲取開發者模式選單項目
const getDeveloperModeItems = () => {
    if (!developerMode.value) return [];

    return [
        {
            name: 'Example',
            meta: {
                title: '元件範例',
                icon: 'ExperimentOutlined'
            }
        }
    ];
};

// 修改選單點擊處理，支援開發者模式路由
const handleMenuClick = (route) => {
    // 如果路由需要參數（如 id），則跳過導航
    if (route.meta?.requiresParam) {
        console.log(
            `Route ${route.name} requires parameters, skipping navigation`
        );
        return;
    }

    console.log('Navigating to route:', route.name, route.path);

    if (route.meta?.externalRoute) {
        router.push({ name: route.meta.externalRoute });
        return;
    }

    if (route.name === 'Example') {
        router.push('/example');
    } else if (route.redirect) {
        // 如果有重定向設定，使用重定向路徑
        console.log('Using redirect:', route.redirect);
        router.push(route.redirect);
    } else if (route.name) {
        // 優先使用名稱導航，這樣可以確保正確的路徑
        console.log('Using name navigation:', route.name);
        router.push({ name: route.name });
    } else if (route.path) {
        // 如果沒有名稱，才使用路徑導航
        console.log('Using path navigation:', route.path);
        router.push(route.path);
    }
};

// 生成子選單內容的 HTML（保留供舊 tooltip 參考，收合彈層已改遞迴元件）
const generateSubmenuContent = (route) => {
    if (!route.children || route.children.length === 0) return '';

    return route.children
        .map(
            (child) =>
                `<div class="vaporwave-submenu-item" data-route="${child.name}">
            <span class="submenu-item-text">${child.meta?.title || child.name}</span>
        </div>`
        )
        .join('');
};

// 處理子選單項目點擊
const handleSubmenuItemClick = (routeName) => {
    router.push({ name: routeName });
    closeCollapsedPopover();
};

const collapsedSubmenuTitles = ref({});

const getCollapsedSubmenuTitle = (route) =>
    collapsedSubmenuTitles.value[route.name] || route.meta?.title || route.name;

const handleCollapsedSubmenuTitleChange = (routeName, title) => {
    collapsedSubmenuTitles.value = {
        ...collapsedSubmenuTitles.value,
        [routeName]: title
    };
};

// 設定彈出容器的位置
const getPopupContainer = (triggerNode) => {
    return triggerNode.parentNode;
};

// 監聽 collapsed 變化，切換狀態時清掉 openKeys 和 popover，避免殘影/閃動
watch(
    () => props.collapsed,
    (isCollapsed) => {
        if (isCollapsed) {
            openKeys.value = [];
        }
        closeCollapsedPopover();
    },
    { immediate: true }
);

onMounted(() => {
    document.addEventListener('keydown', onCollapsedPopoverKeydown);
});

onUnmounted(() => {
    closeCollapsedPopover();
    document.removeEventListener('keydown', onCollapsedPopoverKeydown);
    window.removeEventListener('scroll', onCollapsedViewportChange, true);
    window.removeEventListener('resize', onCollapsedViewportChange);
});
</script>

<template>
    <div :class="['side-menu-wrap', { 'is-collapsed': collapsed }]">
        <a-menu
            v-model:selectedKeys="selectedKeys"
            :openKeys="collapsed ? [] : openKeys"
            @update:openKeys="
                (keys) => {
                    if (!collapsed) openKeys = keys;
                }
            "
            theme="dark"
            mode="inline"
            class="vaporwave-side-menu mt-2 flex-1 overflow-y-auto z-0 hide-scrollbar h-[calc(100vh-64px)] pb-8"
            :class="{ 'overflow-visible': collapsed }"
            :get-popup-container="getPopupContainer"
            :popup-class-name="collapsed ? 'disabled-popup' : ''"
            :inline-collapsed="collapsed"
            :triggerSubMenuAction="collapsed ? 'click' : 'hover'"
            @openChange="
                (keys) => {
                    if (!collapsed) handleOpenChange(keys);
                }
            "
        >
            <!-- 收合狀態下使用自定義 Popover -->
            <template v-if="collapsed">
                <template v-for="route in menuItems" :key="route.name">
                    <template
                        v-if="getCollapsedSubmenuTree(route.name).length > 0"
                    >
                        <!-- 有子選單時使用自定義 Popover，手動控制避免閃動 -->
                        <div
                            class="popover-wrapper collapsed-popover-trigger"
                            role="button"
                            tabindex="0"
                            :aria-expanded="activePopoverKey === route.name"
                            @click.stop="
                                (event) =>
                                    toggleCollapsedPopover(route.name, event)
                            "
                            @keydown.enter.prevent="
                                (event) =>
                                    toggleCollapsedPopover(route.name, event)
                            "
                            @keydown.space.prevent="
                                (event) =>
                                    toggleCollapsedPopover(route.name, event)
                            "
                        >
                            <div
                                class="collapsed-menu-item-wrapper no-tooltip-wrapper"
                                :class="{
                                    'collapsed-menu-item-wrapper--active':
                                        activePopoverKey === route.name
                                }"
                            >
                                <div class="menu-item-content">
                                    <component
                                        v-if="route.meta?.icon"
                                        :is="route.meta.icon"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <!-- 單一選項使用 a-menu-item 的 title 屬性 -->
                        <a-menu-item
                            :key="`item-${route.name}`"
                            @click="handleMenuClick(route)"
                            class="collapsed-menu-item"
                            :title="route.meta?.title"
                        >
                            <div class="menu-item-content">
                                <component
                                    v-if="route.meta?.icon"
                                    :is="route.meta.icon"
                                />
                            </div>
                        </a-menu-item>
                    </template>
                </template>
            </template>

            <!-- 展開狀態下使用原本的遞迴選單 -->
            <template v-else>
                <recursive-menu-item
                    v-for="route in menuItems"
                    :key="route.name"
                    :route="route"
                    :collapsed="collapsed"
                    @menu-click="handleMenuClick"
                />
            </template>
        </a-menu>

        <Teleport to="body">
            <div
                v-if="collapsed && activePopoverKey && activePopoverRoute"
                class="collapsed-submenu-floating vaporwave-custom-popover"
                :style="collapsedFloatingStyle"
                @click.stop
            >
                <div class="custom-submenu-content">
                    <div class="submenu-folder-title">
                        {{ getCollapsedSubmenuTitle(activePopoverRoute) }}
                    </div>
                    <CollapsedSubmenuPanel
                        :nodes="getCollapsedSubmenuTree(activePopoverKey)"
                        :root-title="
                            activePopoverRoute.meta?.title ||
                            activePopoverRoute.name
                        "
                        :active="true"
                        @item-click="handleSubmenuItemClick"
                        @title-change="
                            (title) =>
                                handleCollapsedSubmenuTitleChange(
                                    activePopoverKey,
                                    title
                                )
                        "
                    />
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.hide-scrollbar {
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari and Opera */
}

/* 蒸氣波側選單樣式 */
.vaporwave-side-menu {
    background: transparent !important;
    font-family: var(--font-ui);
    padding-bottom: 2rem !important;
    /* 額外的底部間距 */
}

/* 收合狀態下允許 popup 溢出 */
.vaporwave-side-menu.overflow-visible {
    overflow: visible !important;
}

/* 確保選單項目容器不會阻擋 popup */
.collapsed-menu-item-wrapper {
    position: relative;
    z-index: 1;
}

/* 選單項目基礎樣式 */
:deep(.ant-menu.ant-menu-dark) {
    background: transparent !important;
    border-right: none;
}

:deep(.ant-menu-dark .ant-menu-sub) {
    background: transparent !important;
    backdrop-filter: none;
    border: none !important;
    border-radius: 0;
    margin: 0;
    padding: 8px 0 !important;
    box-shadow: none;
}

/* 子選單：扁平，不要每項白卡片邊框 */
:deep(.ant-menu-dark .ant-menu-sub .ant-menu-item),
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-item),
:deep(.ant-menu-submenu .ant-menu-item),
:deep(.ant-menu-sub .ant-menu-item) {
    background: transparent !important;
    background-color: transparent !important;
    color: #3a4450 !important;
    transition: background 0.15s ease, color 0.15s ease !important;
    border-radius: 8px !important;
    margin: 2px 10px !important;
    padding: 8px 12px !important;
    border: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    width: calc(100% - 20px) !important;
    box-sizing: border-box !important;
    box-shadow: none !important;
}

/* 移除額外的間距，使用統一的 margin */

/* 確保子選項文字內容正確對齊 */
:deep(.ant-menu-dark .ant-menu-sub .ant-menu-item .ant-menu-title-content),
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-item .ant-menu-title-content),
:deep(.ant-menu-submenu .ant-menu-item .ant-menu-title-content),
:deep(.ant-menu-sub .ant-menu-item .ant-menu-title-content) {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    width: 100% !important;
    color: inherit !important;
    font-weight: inherit !important;
}

:deep(.ant-menu-dark .ant-menu-sub .ant-menu-item:hover),
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-item:hover),
:deep(.ant-menu-submenu .ant-menu-item:hover),
:deep(.ant-menu-sub .ant-menu-item:hover) {
    background: rgba(36, 90, 140, 0.08) !important;
    background-color: rgba(36, 90, 140, 0.08) !important;
    color: #152536 !important;
    border: none !important;
    font-weight: 600 !important;
}

:deep(.ant-menu-dark .ant-menu-sub .ant-menu-item-selected),
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-item-selected),
:deep(.ant-menu-submenu .ant-menu-item-selected),
:deep(.ant-menu-sub .ant-menu-item-selected) {
    background: #e4eef6 !important;
    background-color: #e4eef6 !important;
    color: #1a4268 !important;
    border: none !important;
    box-shadow: inset 3px 0 0 #245a8c !important;
    font-weight: 700 !important;
}

:deep(.ant-menu-dark .ant-menu-sub .ant-menu-item:active),
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-item:active),
:deep(.ant-menu-submenu .ant-menu-item:active),
:deep(.ant-menu-sub .ant-menu-item:active) {
    background: #d5e6f3 !important;
    background-color: #d5e6f3 !important;
    color: #152536 !important;
    font-weight: 700 !important;
}

/* 移除 Ant Design 預設的藍色選中樣式 */
:deep(.ant-menu-dark .ant-menu-sub .ant-menu-item::after),
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-item::after),
:deep(.ant-menu-submenu .ant-menu-item::after),
:deep(.ant-menu-sub .ant-menu-item::after) {
    border-right: none !important;
}

/* 子選單容器：透明嵌套，不要白卡片包一層 */
:deep(.ant-menu-dark .ant-menu-submenu .ant-menu-sub),
:deep(.ant-menu-submenu .ant-menu-sub) {
    background: transparent !important;
    backdrop-filter: none;
    border: none !important;
    border-radius: 0;
    margin: 0 0 4px;
    padding: 2px 0 6px !important;
    box-shadow: none !important;
}

/* 選單項目樣式 */
:deep(.ant-menu-item),
:deep(.ant-menu-submenu-title) {
    padding: 0 !important;
    /* 移除內邊距，交由內部 div 控制 */
    margin: 6px 8px !important;
    width: calc(100% - 16px) !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
    position: relative;
    overflow: hidden;
    border: 2px solid transparent !important;
    /* 預留邊框空間，避免 hover/active 時跳動 */
    background: transparent !important;
}

/* 收合狀態下的選單項目間距優化 */
:deep(.ant-menu-inline-collapsed .ant-menu-item),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title) {
    margin: 8px 12px !important;
    width: calc(100% - 24px) !important;
    max-width: 48px !important;
    height: 48px !important;
    border-radius: 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
}

/* 收合狀態下的置中樣式 */
:deep(.ant-menu-inline-collapsed .ant-menu-item .menu-item-content),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title .menu-item-content) {
    @apply justify-center gap-0 p-0 w-full flex items-center;
    height: 100%;
    min-height: 48px;
    /* 確保有足夠高度 */
    position: relative !important;
    /* 為絕對定位的 icon 提供參考點 */
    width: 100% !important;
    margin: 0 !important;
}

/* 控制台 icon */
:deep(.ant-menu-inline-collapsed .ant-menu-item .menu-item-content .anticon) {
    @apply m-0 text-lg;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
}

/* 其他有子選單的 icon */
.submenu-with-icon .menu-item-content .anticon,
:deep(
    .ant-menu-inline-collapsed
        .ant-menu-submenu-title
        .menu-item-content
        .anticon
),
:deep(
    .ant-menu-inline-collapsed .ant-menu-submenu-title .menu-item-content svg
),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title .menu-item-content i) {
    @apply m-0 text-lg;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -35%) !important;
    z-index: 10 !important;
}

/* 確保收合狀態下選單項目寬度正確 */
:deep(.ant-menu-inline-collapsed .ant-menu-item),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title) {
    @apply w-full text-center p-0;
}

/* 移除 Ant Design 預設的內邊距 */
:deep(.ant-menu-inline-collapsed .ant-menu-item),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title) {
    @apply pl-0 pr-0;
}

/* 確保選單項目內容完全置中 */
:deep(.ant-menu-inline-collapsed .ant-menu-item > *),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title > *) {
    @apply flex justify-center items-center w-full;
}

/* 選單項目 hover 效果 */
:deep(.ant-menu-item:hover),
:deep(.ant-menu-submenu-title:hover) {
    border: none !important;
    box-shadow: none !important;
    background: rgba(42, 74, 122, 0.08) !important;
}

/* 收合狀態下選中項目的背景 */
:deep(.ant-menu-inline-collapsed .ant-menu-item-selected),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title-selected) {
    background: rgba(42, 74, 122, 0.1) !important;
    border: none !important;
    box-shadow: none !important;
}

/* 收合狀態下的 hover 效果 */
:deep(.ant-menu-inline-collapsed .ant-menu-item:hover),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title:hover) {
    border-color: var(--neon-purple) !important;
    box-shadow: none !important;
    background: rgba(42, 74, 122, 0.04) !important;
}

/* 選中(Active)的選單項目 */
:deep(.ant-menu-item-selected) {
    border: none !important;
    box-shadow: inset 3px 0 0 #245a8c !important;
    background: #e4eef6 !important;
}

/* 移除 Ant Design 預設的藍色 active/selected 樣式 */
:deep(.ant-menu-dark.ant-menu-inline .ant-menu-item::after) {
    border-right: none !important;
}

/* 勿把選中背景清成透明（會造成破版／重疊） */
:deep(.ant-menu-dark:not(.ant-menu-horizontal) .ant-menu-item-selected) {
    background-color: #e4eef6 !important;
}

/* 移除 focus 時的藍色外框 */
:deep(.ant-menu-item:focus-visible) {
    outline: none !important;
}

/* 統一設定內容區域的樣式 */
:deep(.menu-item-content) {
    padding: 10px 16px;
    color: #4a4a5a;
    transition: all 0.3s ease;
}

:deep(.menu-item-content .anticon) {
    transition: all 0.3s ease;
}

/* Hover 狀態下的內容樣式 */
:deep(.ant-menu-item:hover .menu-item-content),
:deep(.ant-menu-submenu-title:hover .menu-item-content) {
    color: #2a2a3a !important;
}

:deep(.ant-menu-item:hover .menu-item-content .anticon),
:deep(.ant-menu-submenu-title:hover .menu-item-content .anticon) {
    color: #2a2a3a !important;
}

:deep(.ant-menu-item:hover .menu-item-content span),
:deep(.ant-menu-submenu-title:hover .menu-item-content span) {
    color: #2a2a3a !important;
}

/* 收合狀態下的 hover 內容樣式 */
:deep(.ant-menu-inline-collapsed .ant-menu-item:hover .menu-item-content),
:deep(
    .ant-menu-inline-collapsed .ant-menu-submenu-title:hover .menu-item-content
) {
    color: #2a2a3a !important;
}

:deep(
    .ant-menu-inline-collapsed .ant-menu-item:hover .menu-item-content .anticon
),
:deep(
    .ant-menu-inline-collapsed
        .ant-menu-submenu-title:hover
        .menu-item-content
        .anticon
) {
    color: #2a2a3a !important;
}

/* Active 狀態下的內容樣式 */
 :deep(.ant-menu-item-selected .menu-item-content) {
    color: #1a4268;
}

:deep(.ant-menu-item-selected .menu-item-content .anticon) {
    color: #245a8c;
}

:deep(.ant-menu-item-selected .menu-item-content span) {
    color: #1a4268;
    font-weight: 600;
}

/* 選中項：短標題不跑馬燈，避免「核銷 兌換核銷」重疊外溢 */
:deep(.ant-menu-item-selected .menu-title-clip) {
    overflow: hidden;
}

:deep(.ant-menu-item-selected .menu-title-marquee) {
    max-width: 100%;
    animation: none;
    transform: none;
}

:deep(.ant-menu-item-selected .menu-title-label) {
    overflow: hidden;
    text-overflow: ellipsis;
}

:deep(.ant-menu-item-selected .menu-title-ghost) {
    display: none;
}

/* 開發者模式相關樣式 */
.developer-mode-separator {
    height: 1px;
    background: linear-gradient(
        to right,
        transparent,
        var(--sunset-orange),
        transparent
    );
    margin: 8px 16px;
    opacity: 0.6;
}

.developer-mode-trigger {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 48px;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px;
    margin: 4px 8px;
    border: 2px solid transparent;
    background: transparent;
}

.developer-mode-trigger:hover {
    border-color: var(--sunset-orange);
    box-shadow: 0 0 12px rgba(255, 168, 122, 0.3);
}

.developer-mode-active {
    border-color: var(--sunset-orange) !important;
    box-shadow: 0 0 15px rgba(255, 168, 122, 0.4) !important;
    background: rgba(255, 168, 122, 0.1) !important;
}

.trigger-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 8px;
    padding: 0 16px;
    color: rgba(255, 255, 255, 0.5);
    transition: all 0.3s ease;
}

.developer-mode-trigger:hover .trigger-content {
    color: var(--sunset-orange);
}

.developer-mode-active .trigger-content {
    color: var(--sunset-orange);
}

.trigger-icon {
    font-size: 16px;
    transition: all 0.3s ease;
}

.developer-mode-trigger:hover .trigger-icon {
    color: var(--sunset-orange);
    transform: rotate(15deg);
}

.developer-mode-active .trigger-icon {
    color: var(--sunset-orange);
    animation: developerGlow 2s ease-in-out infinite;
}

.trigger-text {
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.click-counter {
    font-size: 10px;
    font-weight: bold;
    color: var(--sunset-orange);
    animation: counterPulse 0.5s ease-in-out;
}

@keyframes counterPulse {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.2);
    }

    100% {
        transform: scale(1);
    }
}

@keyframes developerGlow {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }
}

/* 響應式設計 */
@media (max-width: 768px) {
    :deep(.ant-menu-item),
    :deep(.ant-menu-submenu-title) {
        padding: 8px 16px !important;
        margin: 4px 8px !important;
    }

    :deep(.ant-menu-dark .ant-menu-sub .ant-menu-item) {
        padding: 6px 12px !important;
    }
}

/* Popover wrapper 用於創建統一的 hover 區域 */
.popover-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
}

/* 收合狀態下的選單項目樣式 */
.collapsed-menu-item-wrapper {
    @apply relative;
    width: calc(100% - 24px) !important;
    max-width: 48px !important;
    height: 48px !important;
    margin: 8px 12px !important;
    border-radius: 12px !important;
    transition: all 0.3s ease !important;
    border: none !important;
    background: transparent !important;
    backdrop-filter: none !important;
}

.collapsed-menu-item-wrapper:hover {
    background: rgba(42, 74, 122, 0.08) !important;
}

.collapsed-menu-item-wrapper--active {
    background: rgba(42, 74, 122, 0.1) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.35);
}

.collapsed-popover-trigger {
    cursor: pointer;
    outline: none;
}

.collapsed-popover-trigger:focus-visible .collapsed-menu-item-wrapper {
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.45);
}

.collapsed-submenu-floating {
    position: fixed;
    z-index: 1200;
    width: 220px;
    max-width: calc(100vw - 16px);
    pointer-events: auto;
}

.collapsed-menu-item {
    @apply text-center p-0;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
}

.collapsed-menu-item .menu-item-content {
    @apply justify-center items-center gap-0 p-0 w-full flex;
    height: 100%;
    width: 100% !important;
    margin: 0 !important;
}

.collapsed-menu-item .menu-item-content .anticon {
    @apply m-0 text-lg flex items-center justify-center;
    font-size: 20px !important;
}

/* 收合狀態下選單項目的 hover 效果 */
:deep(.ant-menu-inline-collapsed .ant-menu-item:hover),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title:hover) {
    background: rgba(42, 74, 122, 0.08) !important;
    border: none !important;
    box-shadow: none !important;
}

:deep(.ant-menu-inline-collapsed .ant-menu-item:hover .anticon),
:deep(.ant-menu-inline-collapsed .ant-menu-submenu-title:hover .anticon) {
    color: var(--neon-purple) !important;
    text-shadow: none !important;
}

/* Ant Design Vue Tooltip 蒸氣波樣式 */
:deep(.ant-tooltip) {
    z-index: 99999999 !important;
    pointer-events: auto !important;
    /* 確保可以點擊 */
}

:deep(.ant-tooltip-inner) {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(21, 37, 54, 0.14) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 24px rgba(21, 37, 54, 0.1) !important;
    color: #4a4a5a !important;
    font-weight: bold !important;
    text-shadow: none !important;
    font-size: 14px !important;
    padding: 8px 12px !important;
    min-width: 80px !important;
    text-align: center !important;
}

:deep(.ant-tooltip-arrow::before) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(21, 37, 54, 0.14) !important;
}

/* 確保 tooltip 有足夠的 hover 時間 */
:deep(.ant-tooltip) {
    transition-delay: 0.1s !important;
}

/* Ant Design Vue SubMenu Popup 蒸氣波樣式 - 重新設計 */
:deep(.ant-menu-submenu-popup) {
    background: rgba(255, 255, 255, 0.95) !important;
    background-color: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(25px) !important;
    border: 1px solid rgba(21, 37, 54, 0.14) !important;
    border-radius: 16px !important;
    box-shadow: 0 10px 28px rgba(21, 37, 54, 0.12) !important;
    z-index: 99999999 !important;
    padding: 12px 8px !important;
    min-width: 200px !important;
    font-family: var(--font-ui) !important;
}

/* 針對實際的 DOM 結構：ant-menu-submenu-popup > ul.ant-menu-sub > li.ant-menu-item */
:deep(.ant-menu-submenu-popup .ant-menu-sub) {
    background: transparent !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
}

:deep(.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item) {
    color: #4a4a5a !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    padding: 12px 16px !important;
    margin: 4px 0 !important;
    border-radius: 10px !important;
    font-weight: 500 !important;
    background: rgba(255, 255, 255, 0.8) !important;
    background-color: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(42, 74, 122, 0.18) !important;
    position: relative !important;
    overflow: hidden !important;
}

:deep(.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    background-color: rgba(42, 74, 122, 0.1) !important;
    color: #2a2a3a !important;
    border-color: var(--neon-purple) !important;
    font-weight: bold !important;
    transform: translateX(4px) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.18) !important;
}

:deep(.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item:active) {
    background: rgba(42, 74, 122, 0.16) !important;
    background-color: rgba(42, 74, 122, 0.16) !important;
    color: #2a2a3a !important;
    font-weight: bold !important;
    transform: translateX(2px) !important;
}

:deep(.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item-selected) {
    background: rgba(42, 74, 122, 0.14) !important;
    background-color: rgba(42, 74, 122, 0.14) !important;
    color: #2a2a3a !important;
    border-color: var(--neon-purple) !important;
    font-weight: bold !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.22) !important;
}

/* 移除 Ant Design 預設的藍色邊框 */
:deep(.ant-menu-submenu-popup .ant-menu-sub .ant-menu-item::after) {
    border-right: none !important;
}

/* 針對 ant-menu-title-content 的樣式 */
:deep(
    .ant-menu-submenu-popup .ant-menu-sub .ant-menu-item .ant-menu-title-content
) {
    color: inherit !important;
    font-weight: inherit !important;
}

.vaporwave-submenu-title {
    color: #4a4a5a;
    font-weight: bold;
    text-shadow: none;
    border-bottom: 1px solid rgba(42, 74, 122, 0.18);
    margin-bottom: 8px;
    padding-bottom: 8px;
    font-size: 14px;
}

/* 單一項目標題樣式 */
.vaporwave-single-item-tooltip {
    padding: 8px 12px;
    text-align: center;
}

.single-item-text {
    color: #4a4a5a;
    font-weight: bold;
    text-shadow: none;
    font-size: 14px;
}

/* 禁用 Ant Design 預設的 tooltip */
:deep(.ant-menu-inline-collapsed .ant-menu-item) {
    pointer-events: auto !important;
}

:deep(.ant-menu-inline-collapsed .ant-menu-item[title]) {
    pointer-events: auto !important;
}

/* 收合狀態下禁用有 Popover 的 menu-item 的 tooltip，避免與 Popover 衝突 */
:deep(.ant-menu-inline-collapsed .collapsed-menu-item) {
    pointer-events: auto !important;
}

/* 強制隱藏所有與 no-tooltip-wrapper 相關的 tooltip */
:deep(.no-tooltip-wrapper:hover ~ .ant-tooltip),
:deep(.no-tooltip-wrapper + .ant-tooltip),
:deep(.no-tooltip-wrapper .ant-tooltip),
:deep(.ant-menu-inline-collapsed .no-tooltip-wrapper:hover ~ .ant-tooltip),
:deep(.ant-menu-inline-collapsed .no-tooltip-wrapper + .ant-tooltip) {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
}

/* 確保 no-tooltip-wrapper 不會觸發 tooltip */
:deep(.no-tooltip-wrapper) {
    pointer-events: auto !important;
}

/* Popover 樣式 */
:deep(.vaporwave-custom-popover) {
    background: #ffffff !important;
    background-color: #ffffff !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(21, 37, 54, 0.14) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 28px rgba(42, 42, 58, 0.18) !important;
    padding: 0 !important;
    z-index: 1200 !important;
}

:deep(.vaporwave-custom-popover .ant-popover),
:deep(.vaporwave-custom-popover .ant-popover-inner),
:deep(.vaporwave-custom-popover .ant-popover-inner-content) {
    background: #ffffff !important;
    background-color: #ffffff !important;
}

:deep(.vaporwave-custom-popover .ant-popover-inner) {
    border: none !important;
    border-radius: 12px !important;
    padding: 12px 8px !important;
}

.custom-submenu-content {
    min-width: 180px;
    padding: 4px 0;
}

.submenu-folder-title {
    color: #2a4a7a;
    font-weight: 700;
    font-size: 14px;
    padding: 8px 12px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.14);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.custom-submenu-item {
    padding: 10px 16px;
    margin: 4px 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #4a4a5a;
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.8);
    font-weight: 500;
}

.custom-submenu-item:hover {
    background: rgba(42, 74, 122, 0.1) !important;
    color: #2a2a3a !important;
    border-color: var(--neon-purple) !important;
    transform: translateX(4px);
    box-shadow: 0 0 12px rgba(42, 74, 122, 0.14);
}

/* Popover 樣式 */
:deep(.vaporwave-submenu-popover) {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(21, 37, 54, 0.14) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 24px rgba(21, 37, 54, 0.1) !important;
    padding: 0 !important;
    z-index: 9999 !important;
    margin-left: 8px !important;
    /* 與側邊欄保持間距 */
}

:deep(.vaporwave-submenu-popover .ant-popover-inner) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(21, 37, 54, 0.14) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 24px rgba(21, 37, 54, 0.1) !important;
    padding: 12px !important;
}

:deep(.vaporwave-submenu-popover .ant-popover-title) {
    color: #4a4a5a !important;
    font-weight: bold !important;
    text-shadow: none !important;
    border-bottom: 1px solid rgba(42, 74, 122, 0.18) !important;
    margin-bottom: 8px !important;
    padding-bottom: 8px !important;
    font-size: 14px !important;
}

:deep(.vaporwave-submenu-popover .ant-popover-arrow) {
    display: none !important;
}

.vaporwave-submenu-content {
    min-width: 160px;
}

.vaporwave-submenu-item {
    padding: 8px 12px;
    margin: 2px 0;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #4a4a5a;
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.8);
}

.vaporwave-submenu-item:hover {
    background: rgba(42, 74, 122, 0.1) !important;
    color: #2a2a3a !important;
    border-color: var(--neon-purple) !important;
    text-shadow: none !important;
}

.submenu-item-text {
    font-size: 14px;
    font-weight: 500;
}

/* 動畫效果 - 已移除 */


</style>
